import { type Map as ImmutableMap, List, type RecordOf } from 'immutable';

import { createAppSelector } from 'app-components/utils/core/reselect';
import type RootState from 'app-components/utils/redux-store/rootState';

import { accountsSelectors, accountsUtils } from '../accounts';
import type { Account } from '../accounts/accountsTypes';
import { entitlementsSelectors } from '../entitlements';
import { institutionLoginsSelectors } from '../institution-logins';
import type { MfaChallenge } from '../institution-logins/institutionLoginsTypes';
import { institutionsUtils } from '../institutions';
import type { Institution, InstitutionChannelData } from '../institutions/institutionsTypes';
import {
  ACCOUNT_DISCOVERY_REDUCER_KEY,
  type AccountData,
  type AccountDiscoveryData,
  type ActiveDiscoveredAccount,
  type AggAction,
  type DiscoveryInstitutionLogin,
  type InstitutionLoginUpserterData,
  type InstitutionLoginUpserterStore,
} from './accountDiscoveryTypes';
import buildUpsertableAccountData, { isMaskedNumberMatch } from './buildUpsertableAccountData';

type State = Pick<RootState, typeof ACCOUNT_DISCOVERY_REDUCER_KEY>;

type ScopeProps = {
  scope: string;
};

type InstitutionAggregatorInfo = {
  channelDataByType?: ImmutableMap<string, RecordOf<InstitutionChannelData>>;
  channelData?: RecordOf<InstitutionChannelData>;
};

export const getInstitutionLoginUpserterStore = (state: State): InstitutionLoginUpserterStore =>
  state[ACCOUNT_DISCOVERY_REDUCER_KEY];

export const getInstitutionLoginUpserterDataForScope = (
  state: State,
  { scope }: ScopeProps,
): InstitutionLoginUpserterData | undefined => getInstitutionLoginUpserterStore(state).upserterData.get(scope);

export const getInstitution = (state: State, props: ScopeProps): Institution | RecordOf<Institution> | undefined =>
  getInstitutionLoginUpserterDataForScope(state, props)?.institution;
export const getChannel = (state: State, props: ScopeProps): string | undefined =>
  getInstitutionLoginUpserterDataForScope(state, props)?.channel;

export const getInstitutionLogin = (state: State, props: ScopeProps): DiscoveryInstitutionLogin | undefined | null =>
  getInstitutionLoginUpserterDataForScope(state, props)?.institutionLogin;
export const getMfaChallenge = (
  state: State,
  props: ScopeProps,
): MfaChallenge | RecordOf<MfaChallenge> | null | undefined =>
  getInstitutionLoginUpserterDataForScope(state, props)?.mfaChallenge;
export const getInstitutionLoginIdBeingMigrated = (state: State, props: ScopeProps): string | undefined =>
  getInstitutionLoginUpserterDataForScope(state, props)?.institutionLoginIdBeingMigrated;

export const getPartnerAuthUris = (state: State, props: ScopeProps): List<string> | undefined =>
  getInstitutionLoginUpserterDataForScope(state, props)?.partnerAuthUris;
export const getAccountDiscoveryData = (state: State, props: ScopeProps): AccountDiscoveryData | null | undefined =>
  getInstitutionLoginUpserterDataForScope(state, props)?.accountDiscoveryData;

export const getIsAuthenticationDone = (state: State, props: ScopeProps): boolean | undefined =>
  getInstitutionLoginUpserterDataForScope(state, props)?.isAuthenticationDone;
export const getIsAccountUpsertingDone = (state: State, props: ScopeProps): boolean | undefined =>
  getInstitutionLoginUpserterDataForScope(state, props)?.isAccountUpsertingDone;

export const getIsPolling = (state: State, props: ScopeProps): boolean | undefined =>
  getInstitutionLoginUpserterDataForScope(state, props)?.isPolling;
export const getIsSubmitting = (state: State, props: ScopeProps): boolean | undefined =>
  getInstitutionLoginUpserterDataForScope(state, props)?.isSubmitting;

export const getError = (state: State, props: ScopeProps): AggAction | false | null | undefined =>
  getInstitutionLoginUpserterDataForScope(state, props)?.error;

export const getInstitutionChannelData = createAppSelector(
  getInstitution,
  getChannel,
  (institution, channel): RecordOf<InstitutionChannelData> | undefined => {
    if (!institution || !channel) {
      return undefined;
    }
    const aggregatorKey = institutionsUtils.getAggregatorFromChannel(channel).toLowerCase();
    const aggregatorInfo = institution[aggregatorKey as keyof Institution] as InstitutionAggregatorInfo | undefined;
    return aggregatorInfo?.channelDataByType
      ? aggregatorInfo.channelDataByType.get(channel)
      : aggregatorInfo?.channelData;
  },
);

export const getAccountsBeingMigrated = createAppSelector(
  getInstitutionLoginIdBeingMigrated,
  institutionLoginsSelectors.getInstitutionLoginsById,
  accountsSelectors.getAccountsById,
  (institutionLoginIdBeingMigrated, institutionLoginsById, accountsById): List<Account> | null => {
    if (!institutionLoginIdBeingMigrated) return null;

    return List(
      accountsById.valueSeq().reduce<Account[]>((list, account) => {
        if (account.isClosed) return list;

        if (institutionLoginIdBeingMigrated && account.institutionLoginId === institutionLoginIdBeingMigrated) {
          // account belongs to institution-login being migrated
          list.push(account);
        } else if (account.institutionLoginId && account.institutionLoginId !== '0') {
          const institutionLogin = institutionLoginsById.get(account.institutionLoginId);

          const institutionLoginBeingMigrated = institutionLoginsById.get(institutionLoginIdBeingMigrated);

          if (
            institutionLogin &&
            institutionLoginBeingMigrated &&
            institutionLogin.institutionId === institutionLoginBeingMigrated.institutionId
          ) {
            // account belongs to another institution-login with same FI as the one being migrated
            list.push(account);
          }
        }
        return list;
      }, []),
    );
  },
);

const isFiMigrationAccount = (
  account: Account | undefined,
  institutionLoginsById: ReturnType<typeof institutionLoginsSelectors.getInstitutionLoginsById>,
): boolean =>
  Boolean(
    account &&
      !account.isClosed &&
      account.institutionLoginId &&
      institutionLoginsById.get(account.institutionLoginId)?.fiMigrationInfo,
  );

export const getFiMigrationLinkableAccounts = createAppSelector(
  institutionLoginsSelectors.getInstitutionLoginsById,
  accountsSelectors.getAccountsById,
  (institutionLoginsById, accountsById): List<Account> =>
    List(accountsById.valueSeq().filter((account) => isFiMigrationAccount(account, institutionLoginsById))),
);

export const getIsFiMigrationAccount = (
  state: Parameters<typeof accountsSelectors.getAccountsById>[0] &
    Parameters<typeof institutionLoginsSelectors.getInstitutionLoginsById>[0],
  accountId: string | null | undefined,
): boolean =>
  Boolean(accountId) &&
  isFiMigrationAccount(
    accountsSelectors.getAccountsById(state).get(accountId!),
    institutionLoginsSelectors.getInstitutionLoginsById(state),
  );

// Mint import rows have no discovery scope, so no login is being migrated.
export const getShowCrossLoginLinkWarning = (
  state: State & Parameters<typeof getIsFiMigrationAccount>[0],
  { scope, accountId }: { scope?: string; accountId: string | null | undefined },
): boolean =>
  getIsFiMigrationAccount(state, accountId) &&
  accountsSelectors.getAccountsById(state).get(accountId!)?.institutionLoginId !==
    (scope === undefined ? undefined : getInstitutionLoginIdBeingMigrated(state, { scope }));

const getScopedFiMigrationLinkableAccounts = createAppSelector(
  getInstitutionLoginIdBeingMigrated,
  getFiMigrationLinkableAccounts,
  (institutionLoginIdBeingMigrated, fiMigrationLinkableAccounts): List<Account> =>
    institutionLoginIdBeingMigrated ? List() : fiMigrationLinkableAccounts,
);

export const getFiMigrationMatchedAccounts = createAppSelector(
  getAccountDiscoveryData,
  getScopedFiMigrationLinkableAccounts,
  (accountDiscoveryData, fiMigrationLinkableAccounts): List<Account> => {
    const newAccounts = accountDiscoveryData?.newAccounts;
    if (!newAccounts?.size) return List();
    return fiMigrationLinkableAccounts.filter((account) =>
      newAccounts.some((discoveredAccount) => isMaskedNumberMatch(discoveredAccount, account)),
    );
  },
);

export const getUpsertableNewAccountsData = createAppSelector(
  getAccountDiscoveryData,
  getAccountsBeingMigrated,
  institutionLoginsSelectors.getPureManualAccounts,
  entitlementsSelectors.getBusinessEnabled,
  getScopedFiMigrationLinkableAccounts,
  (
    accountDiscoveryData,
    accountsBeingMigrated,
    fiConnectableAccounts,
    businessEnabled,
    fiMigrationLinkableAccounts,
  ): List<AccountData> => {
    const manualLinkableAccounts = fiConnectableAccounts.filter((acct) => acct.type !== 'GOAL');
    return buildUpsertableAccountData(
      accountDiscoveryData,
      accountsBeingMigrated,
      manualLinkableAccounts,
      businessEnabled,
      fiMigrationLinkableAccounts,
    );
  },
);

export const getActiveAccountsData = createAppSelector(
  getAccountDiscoveryData,
  (accountDiscoveryData): List<ActiveDiscoveredAccount> | null | undefined => accountDiscoveryData?.activeAccounts,
);

export const getDeadAccountsData = createAppSelector(
  getAccountDiscoveryData,
  (accountDiscoveryData): List<Account> | null | undefined => accountDiscoveryData?.deadAccounts,
);

export const getOtherAccountAtSameInstitution = createAppSelector(
  getInstitution,
  getInstitutionLogin,
  getInstitutionLoginIdBeingMigrated,
  institutionLoginsSelectors.getInstitutionLoginsById,
  accountsSelectors.getAccountsByInstitutionLoginId,
  (institution, login, loginIdBeingMigrated, loginsById, accountsByLoginId): List<Account> => {
    const otherLoginsForInstitution = loginsById?.reduce<List<DiscoveryInstitutionLogin>>((list, iLogin) => {
      if (
        iLogin?.id !== login?.id &&
        loginIdBeingMigrated &&
        iLogin?.id !== loginIdBeingMigrated &&
        login?.institutionId === institution!.id
      ) {
        return list.push(login);
      }
      return list;
    }, List<DiscoveryInstitutionLogin>());
    return otherLoginsForInstitution?.flatMap((loginForInstitution) => {
      const accountsforLoginId = loginForInstitution?.id ? accountsByLoginId.get(loginForInstitution.id) : undefined;
      return accountsforLoginId
        ? accountsforLoginId.filter((account: Account) => accountsUtils.isAccountConnected(account))
        : List<Account>();
    });
  },
);
